import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

const api = axios.create({
  baseURL: `${API_BASE_URL}/api/v1`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to all requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Types
export interface LaunchMonitorReviewData {
  launch_angle?: number;
  launch_angle_std_dev?: number;
  spin_rate?: number;
  spin_rate_std_dev?: number;
  ball_speed?: number;
  ball_speed_std_dev?: number;
  carry_distance?: number;
  carry_std_dev?: number;
  dynamic_loft?: number;
  club_path?: number;
  face_angle?: number;
  face_to_path?: number;
  spin_axis?: number;
  side_deviation?: number;
  dispersion_std_dev?: number;
  smash_factor?: number;
  impact_location_pattern?: string;
  peak_height?: number;
  descent_angle?: number;
  shot_count?: number;
}

export interface PostFittingReviewCreate {
  profile_id: number;
  equipment_type: 'driver' | 'iron' | 'wedge';
  recommended_head_id?: number;
  recommended_head_brand?: string;
  recommended_head_model?: string;
  recommended_head_loft?: number;
  recommended_shaft_id?: number;
  recommended_shaft_brand?: string;
  recommended_shaft_model?: string;
  recommended_shaft_flex?: string;
  recommended_shaft_weight?: number;
  recommended_combined_score?: number;
  tried_head_brand?: string;
  tried_head_model?: string;
  tried_head_loft?: number;
  tried_shaft_brand?: string;
  tried_shaft_model?: string;
  tried_shaft_flex?: string;
  tried_shaft_weight?: number;
  user_rating?: number;
  did_purchase?: boolean;
  user_notes?: string;
  has_launch_monitor: boolean;
  launch_monitor_data?: LaunchMonitorReviewData;
  head_rankings_json?: string;
  shaft_rankings_json?: string;
}

export interface PostFittingReview {
  id: number;
  user_id: number;
  profile_id: number;
  equipment_type: string;
  recommended_head_brand?: string;
  recommended_head_model?: string;
  recommended_head_loft?: number;
  recommended_shaft_brand?: string;
  recommended_shaft_model?: string;
  recommended_shaft_flex?: string;
  recommended_shaft_weight?: number;
  recommended_combined_score?: number;
  tried_head_brand?: string;
  tried_head_model?: string;
  tried_head_loft?: number;
  tried_shaft_brand?: string;
  tried_shaft_model?: string;
  tried_shaft_flex?: string;
  tried_shaft_weight?: number;
  user_rating?: number;
  did_purchase?: boolean;
  user_notes?: string;
  has_launch_monitor: boolean;
  launch_angle?: number;
  spin_rate?: number;
  ball_speed?: number;
  carry_distance?: number;
  smash_factor?: number;
  shot_count?: number;
  head_rankings_json?: string;
  shaft_rankings_json?: string;
  re_recommendation_triggered: boolean;
  re_recommendation_profile_id?: number;
  created_at?: string;
  updated_at?: string;
}

// API calls

export const createReview = async (data: PostFittingReviewCreate): Promise<PostFittingReview> => {
  try {
    const response = await api.post('/fitting-review/review/', data);
    return response.data;
  } catch (error: any) {
    console.error('Create review error:', error);
    throw new Error(error.response?.data?.detail || error.message || 'Failed to create review');
  }
};

export const getReviewsForProfile = async (profileId: number): Promise<PostFittingReview[]> => {
  try {
    const response = await api.get(`/fitting-review/review/profile/${profileId}`);
    return response.data;
  } catch (error: any) {
    console.error('Get reviews for profile error:', error);
    throw new Error(error.response?.data?.detail || error.message || 'Failed to get reviews');
  }
};

export const getMyReviews = async (): Promise<PostFittingReview[]> => {
  try {
    const response = await api.get('/fitting-review/review/me');
    return response.data;
  } catch (error: any) {
    console.error('Get my reviews error:', error);
    throw new Error(error.response?.data?.detail || error.message || 'Failed to get reviews');
  }
};

export const updateReview = async (reviewId: number, data: Partial<PostFittingReviewCreate>): Promise<PostFittingReview> => {
  try {
    const response = await api.put(`/fitting-review/review/${reviewId}`, data);
    return response.data;
  } catch (error: any) {
    console.error('Update review error:', error);
    throw new Error(error.response?.data?.detail || error.message || 'Failed to update review');
  }
};

export const deleteReview = async (reviewId: number): Promise<void> => {
  try {
    await api.delete(`/fitting-review/review/${reviewId}`);
  } catch (error: any) {
    console.error('Delete review error:', error);
    throw new Error(error.response?.data?.detail || error.message || 'Failed to delete review');
  }
};

export const triggerReRecommendation = async (reviewId: number): Promise<any> => {
  try {
    const response = await api.post(`/fitting-review/review/${reviewId}/re-recommend`);
    return response.data;
  } catch (error: any) {
    console.error('Re-recommendation error:', error);
    throw new Error(error.response?.data?.detail || error.message || 'Failed to re-run recommendations');
  }
};

export const getRecommendationsForProfile = async (profileId: number): Promise<any> => {
  try {
    const response = await api.get(`/fitting-review/recommendations/${profileId}`);
    return response.data;
  } catch (error: any) {
    console.error('Get recommendations for profile error:', error);
    throw new Error(error.response?.data?.detail || error.message || 'Failed to get recommendations');
  }
};
